// Sentry imports
import * as Sentry from '@sentry/react'
import {browserTracingIntegration, ErrorBoundary as SentryErrorBoundaryComponent, init as sentryInit} from '@sentry/react'

// createBrowserHistory from 'history' is needed to provide a shared history object
// to React Router (via <Router>). Sentry's BrowserTracing will use this.
import {createBrowserHistory} from 'history'

import {getCommitSHA, getEnvironmentType, getSentryDsn, isStagingEnvironment} from './common/environment/environmentManager'

import {connectAuthEmulator, getAuth} from 'firebase/auth'
import React from 'react'
import {createRoot} from 'react-dom/client'
import {Route, Router, Switch} from 'react-router-dom'
import {AppWithProviders} from './AppWithProviders'
import {initializeFirebase} from './common/auth/firebaseConfig'
import {EnvironmentPage} from './features/meta/Environment'
import {initI18N} from './i18n'
import './index.css'
import 'leaflet/dist/leaflet.css'
import reportWebVitals from './reportWebVitals'
import GenerateTestErrorPage from './features/meta/GenerateTestErrorPage'

// --- Sentry Initialization ---
// Create a history instance for React Router v5
const history = createBrowserHistory()

const currentEnv = getEnvironmentType()
const isSentryEnabled = currentEnv !== 'local'
const isSentryDebugEnabled = isSentryEnabled && (currentEnv === 'development' || currentEnv === 'staging')

function decorateFaviconForStaging() {
  const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null
  if (!link) return

  const img = new Image()
  img.crossOrigin = 'anonymous'
  img.src = link.href

  img.onload = () => {
    const size = 64 // render high-res favicon
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.imageSmoothingEnabled = true
    ctx.drawImage(img, 0, 0, size, size)

    const badgeRadius = size * 0.25
    const badgeX = size - badgeRadius - 1
    const badgeY = badgeRadius + 1

    ctx.fillStyle = '#7D20B3'
    ctx.beginPath()
    ctx.arc(badgeX, badgeY, badgeRadius, 0, Math.PI * 2)
    ctx.fill()

    ctx.fillStyle = '#FFFFFF'
    ctx.font = `bold ${Math.round(size * 0.42)}px sans-serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText('S', badgeX, badgeY + 1)

    // Append a fragment (#env) so the favicon URL changes and browsers reload it instead of using a cached icon.
    link.href = canvas.toDataURL('image/png') + '#env'
  }
}

if (isStagingEnvironment()) {
  decorateFaviconForStaging()
}

sentryInit({
  dsn: getSentryDsn(),
  enabled: isSentryEnabled,
  integrations: [browserTracingIntegration()],
  // tracesSampleRate is for performance monitoring (BrowserTracing)
  tracesSampleRate: currentEnv === 'production' ? 0.1 : 1.0,

  environment: currentEnv,
  release: `piffl-admin@${getCommitSHA() || 'unknown-commit'}`,
  debug: isSentryDebugEnabled,
})
// --- End Sentry Initialization ---

initializeFirebase()
initI18N()

const shouldUseFirebaseEmulator = false //Hardcoded to false for now. TODO: Do we need this?
if (shouldUseFirebaseEmulator) {
  connectAuthEmulator(getAuth(), 'http://localhost:9099')
}

interface MySentryFallbackProps {
  error: unknown
  componentStack: string | null
  eventId: string | null
  resetError: () => void
}

function MySentryFallbackComponent({error, eventId, resetError, componentStack}: MySentryFallbackProps) {
  const environmentForFallback = getEnvironmentType()
  const showDevDetails = environmentForFallback !== 'production' && environmentForFallback !== 'local'

  if (showDevDetails && componentStack) {
    console.error('Sentry Fallback Component Stack:', componentStack)
  }

  const getErrorMessage = (err: unknown): string => {
    if (err instanceof Error) {
      return err.message
    }
    return String(err)
  }

  const getErrorStack = (err: unknown): string | undefined => {
    if (err instanceof Error) {
      return err.stack
    }
    return undefined
  }

  const errorDetailsForRender =
    showDevDetails && error ? (
      <details
        style={{
          marginTop: '20px',
          textAlign: 'left',
          border: '1px solid #ddd',
          padding: '10px',
          borderRadius: '4px',
          background: '#f9f9f9',
        }}
      >
        <summary style={{cursor: 'pointer', fontWeight: 'bold'}}>Error Details (Non-Production/Non-Local)</summary>
        <pre
          style={{
            background: '#eee',
            padding: '10px',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            maxHeight: '200px',
            overflowY: 'auto',
            marginTop: '10px',
            border: '1px solid #ccc',
          }}
        >
          {getErrorMessage(error)}
          {getErrorStack(error) && `\n\nStack Trace:\n${getErrorStack(error)}`}
          {componentStack && `\n\nComponent Stack:\n${componentStack}`}
        </pre>
      </details>
    ) : null

  return (
    <div
      style={{
        padding: '20px',
        textAlign: 'center',
        fontFamily: 'Arial, sans-serif',
        color: '#333',
      }}
    >
      <h2 style={{color: '#d9534f'}}>Oops! An Error Occurred</h2>
      <p>We're sorry, something went wrong while trying to load this part of Piffl Admin.</p>
      {isSentryEnabled && <p>Our technical team has been automatically notified of this issue.</p>}
      {isSentryEnabled && eventId && (
        <p>
          If you need to contact support, please provide this Error ID: <strong>{eventId}</strong>
        </p>
      )}
      {!isSentryEnabled && environmentForFallback === 'local' && <p>Sentry reporting is disabled for the local environment.</p>}
      {!isSentryEnabled && environmentForFallback !== 'local' && (
        <p>Please try refreshing the page. If the problem persists, note down the error details if shown below.</p>
      )}

      <p>
        You can try{' '}
        <a
          href={window.location.pathname}
          onClick={(e) => {
            e.preventDefault()
            resetError()
            window.location.reload()
          }}
          style={{color: '#007bff', textDecoration: 'none'}}
        >
          refreshing the page
        </a>{' '}
        or{' '}
        <button
          onClick={resetError}
          style={{
            color: '#007bff',
            background: 'none',
            border: 'none',
            padding: '0',
            font: 'inherit',
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
        >
          try to recover
        </button>
        .
      </p>
      {errorDetailsForRender}
    </div>
  )
}

const container = document.getElementById('root')
const root = createRoot(container!)

type FallbackRender = (errorData: {
  error: unknown
  componentStack: string | null
  eventId: string | null
  resetError: () => void
}) => React.ReactElement

root.render(
  <React.StrictMode>
    <SentryErrorBoundaryComponent fallback={MySentryFallbackComponent as FallbackRender} showDialog={false}>
      <Router history={history}>
        <Switch>
          {/*Hidden routes*/}
          <Route exact path="/meta/environment" component={EnvironmentPage} />
          <Route exact path="/meta/generate_test_error" component={GenerateTestErrorPage} />

          {/*Main application route*/}
          <Route path="/" component={AppWithProviders} />
        </Switch>
      </Router>
    </SentryErrorBoundaryComponent>
  </React.StrictMode>,
)

reportWebVitals((metric) => {
  if (!isSentryEnabled) return

  // Attach Web Vitals as measurements to the active Sentry transaction
  const tx: any = (Sentry as any).getCurrentHub?.().getScope?.()?.getTransaction?.()
  if (tx?.setMeasurement) {
    // unit: CLS has no unit; others are milliseconds
    const unit = metric.name === 'CLS' ? 'none' : 'millisecond'
    tx.setMeasurement(metric.name, metric.value, unit)
  }
})
